import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useLocation } from "react-router";

import { useLocationChange } from "app/hooks/routing/useLocationChange";

const DEFAULT_CONTENT = <div>No help content available for this page</div>;
const LOADING_DELAY_MS = 300;

interface HelpDrawerContextType {
  content: ReactNode;
  isLoading: boolean;
  setContent: (content: ReactNode) => void;
  resetContent: () => void;
}

const HelpDrawerContext = createContext<HelpDrawerContextType | undefined>(
  undefined,
);

export const useHelpDrawer = () => {
  const context = useContext(HelpDrawerContext);

  if (!context) {
    throw new Error("useHelpDrawer must be used within a HelpDrawerProvider");
  }

  return context;
};

export const HelpDrawerProvider = ({ children }: { children: ReactNode }) => {
  const [content, setContent] = useState<ReactNode>(DEFAULT_CONTENT);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track which location the current content is for
  const contentLocationRef = useRef<string | null>(null);
  const location = useLocation();

  const setContentWithLoading = useCallback(
    (newContent: ReactNode) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsLoading(false);
      setContent(newContent);
      // Track that content has been set for the current location
      contentLocationRef.current = location.pathname;
    },
    [location.pathname],
  );

  const resetContent = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Set loading state instead of immediately showing DEFAULT_CONTENT
    setIsLoading(true);
    contentLocationRef.current = null;

    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setContent(DEFAULT_CONTENT);
      timeoutRef.current = null;
    }, LOADING_DELAY_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Automatically clear content when route changes, but only if content
  // hasn't already been set for the new location (to avoid race condition
  // with HelpDrawerContent's useLayoutEffect)
  useLocationChange(
    useCallback(
      (location, prevLocation) => {
        if (
          prevLocation &&
          location.pathname !== prevLocation.pathname &&
          contentLocationRef.current !== location.pathname
        ) {
          resetContent();
        }
      },
      [resetContent],
    ),
  );

  return (
    <HelpDrawerContext.Provider
      value={{
        content,
        isLoading,
        setContent: setContentWithLoading,
        resetContent,
      }}
    >
      {children}
    </HelpDrawerContext.Provider>
  );
};
